import { useEffect } from "react";
import { FaEnvelope, FaLock, FaUser, FaPhone, FaIdCard } from "react-icons/fa";
import {
  Button,
  Form,
  FormItem,
  Input,
  PasswordInput,
  useForm,
  Message,
} from "@/frontend/components/common";
import { userService } from "@/frontend/services";
import { useNavigate } from "react-router-dom";
import type { RegisterRequestDTO, ApiResponse, UserResponseDTO } from "@/shared/dto";

interface UserFormValues {
  email: string;
  username: string;
  password?: string;
  firstName: string;
  lastName: string;
  secondName?: string;
  secondLastName?: string;
  phone?: string;
  dni?: string;
}

interface UserFormProps {
  userId?: string;
  initialValues?: Partial<UserFormValues>;
}

export function UserForm({ userId, initialValues }: UserFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!userId;

  useEffect(() => {
    if (initialValues && isEditMode) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, isEditMode, form]);

  const handleSubmit = async (values: UserFormValues) => {
    try {
      const userData: Partial<RegisterRequestDTO> = {
        email: values.email,
        username: values.username,
        firstName: values.firstName,
        lastName: values.lastName,
        secondName: values.secondName || undefined,
        secondLastName: values.secondLastName || undefined,
        phone: values.phone || undefined,
        dni: values.dni || undefined,
      };

      if (values.password && !isEditMode) {
        userData.password = values.password;
      }

      let response: ApiResponse<UserResponseDTO>;

      if (isEditMode && userId) {
        response = await userService.update(userId, userData);
      } else {

        response = await userService.create(userData as RegisterRequestDTO);
      }

      if (response.success && response.data) {
        if (!isEditMode) {
          Message.success({
            content: "Usuario Creado exitosamente",
          });
          const { id } = response.data;
          navigate(`/users/edit/${id}`);
        } else {
          Message.success({
            content: "Usuario Actualizado exitosamente",
          });
        }
      } else {
        Message.error({
          content:
            response.error ||
            (isEditMode ? "Error al actualizar usuario" : "Error al crear usuario"),
        });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : isEditMode
            ? "Error al actualizar usuario"
            : "Error al crear usuario";

      if (errorMessage.toLowerCase().includes("email")) {
        form.setFields([
          {
            name: "email",
            errors: [errorMessage],
          },
        ]);
      } else {
        form.setFields([
          {
            name: "password",
            errors: [errorMessage],
          },
        ]);
      }
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className="w-full"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Email */}
        <FormItem
          name="email"
          label="Email"
          rules={[
            { required: true, message: "El email es requerido" },
            { type: "email", message: "Email inválido" },
          ]}
        >
          <Input
            prefix={<FaEnvelope className="text-gray-400" />}
            placeholder="correo@ejemplo.com"
            size="large"
          />
        </FormItem>

        {/* Username */}
        <FormItem
          name="username"
          label="Usuario"
          rules={[{ required: true, message: "El usuario es requerido" }]}
        >
          <Input
            prefix={<FaUser className="text-gray-400" />}
            placeholder="Nombre de usuario"
            size="large"
          />
        </FormItem>

        {/* Password */}
        {!isEditMode && (
          <FormItem
            name="password"
            label="Contraseña"
            rules={[
              { required: true, message: "La contraseña es requerida" },
              { min: 8, message: "Mínimo 8 caracteres" },
              {
                pattern: /[0-9]/,
                message: "Debe contener al menos un número",
              },
              {
                pattern: /[a-zA-Z]/,
                message: "Debe contener al menos una letra",
              },
              {
                pattern: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/,
                message: "Debe contener al menos un caracter especial",
              },
            ]}
          >
            <PasswordInput
              prefix={<FaLock className="text-gray-400" />}
              placeholder={isEditMode ? "Dejar vacío para no cambiar" : "Contraseña segura"}
              size="large"
            />
          </FormItem>
        )}

        {/* First Name */}
        <FormItem
          name="firstName"
          label="Nombre"
          rules={[{ required: true, message: "El nombre es requerido" }]}
        >
          <Input
            prefix={<FaUser className="text-gray-400" />}
            placeholder="Nombre"
            size="large"
          />
        </FormItem>

        {/* Last Name */}
        <FormItem
          name="lastName"
          label="Primer Apellido"
          rules={[{ required: true, message: "El apellido es requerido" }]}
        >
          <Input
            prefix={<FaUser className="text-gray-400" />}
            placeholder="Primer apellido"
            size="large"
          />
        </FormItem>

        {/* Second Name */}
        <FormItem
          name="secondName"
          label="Segundo Nombre"
        >
          <Input
            prefix={<FaUser className="text-gray-400" />}
            placeholder="Segundo nombre (opcional)"
            size="large"
          />
        </FormItem>

        {/* Second Last Name */}
        <FormItem
          name="secondLastName"
          label="Segundo Apellido"
        >
          <Input
            prefix={<FaUser className="text-gray-400" />}
            placeholder="Segundo apellido (opcional)"
            size="large"
          />
        </FormItem>

        {/* Phone */}
        <FormItem
          name="phone"
          label="Teléfono"
        >
          <Input
            prefix={<FaPhone className="text-gray-400" />}
            placeholder="Teléfono (opcional)"
            size="large"
          />
        </FormItem>

        {/* DNI */}
        <FormItem
          name="dni"
          label="DNI"
        >
          <Input
            prefix={<FaIdCard className="text-gray-400" />}
            placeholder="DNI (opcional)"
            size="large"
          />
        </FormItem>
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Button
          type="primary"
          htmlType="submit"
          size="large"
        >
          {isEditMode ? "Actualizar Usuario" : "Crear Usuario"}
        </Button>
        <Button
          size="large"
          onClick={() => navigate("/users")}
        >
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
